
import React, { useRef, useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { handleError } from '@/utils/error-handling';
import BreathingCanvas from './BreathingCanvas';
import BreathingInstructions from './BreathingInstructions';
import BreathingStatus from './BreathingStatus';
import { useBreathingLogic } from './useBreathingLogic';
import BreathingError from './components/BreathingError';
import MobileInstructions from '../common/MobileInstructions';

interface BreathingGameProps {
  title: string;
}

const BreathingGame = ({ title }: BreathingGameProps) => {
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();
  
  const {
    breathingPhase,
    secondsLeft,
    cyclesCompleted,
    setBreathingPhase,
    setSecondsLeft,
    setCyclesCompleted
  } = useBreathingLogic();
  
  useEffect(() => {
    try {
      // Initialize the breathing exercise
      if (gameContainerRef.current) {
        setIsLoading(false);
      } else {
        throw new Error("Could not initialize the breathing exercise container");
      }
    } catch (error) {
      console.error("Failed to initialize Breathing game:", error);
      const errorMessage = error instanceof Error ? 
        error.message : 
        "Failed to initialize the Breathing Exercise. Please try refreshing the page.";
      
      setError(errorMessage);
      setIsLoading(false);
      
      // Report error to centralized error handler
      handleError(error, "Failed to initialize Breathing Exercise");
    }
  }, []);
  
  const handleRetry = () => {
    setIsLoading(true);
    setError(null);
    setBreathingPhase('inhale');
    setSecondsLeft(4);
    setCyclesCompleted(0);
    
    // Simulate re-initialization
    setTimeout(() => {
      if (gameContainerRef.current) {
        setIsLoading(false);
      } else {
        setError("Could not reinitialize breathing exercise. Please refresh the page.");
        handleError("Container ref not available", "Could not restart breathing exercise");
      }
    }, 1000);
  };

  // If there's an error, show the error component
  if (error) {
    return <BreathingError errorMessage={error} onRetry={handleRetry} />;
  }

  return (
    <div 
      className="w-full h-full flex flex-col items-center justify-center relative"
      role="application"
      aria-label="Breathing Exercise"
    >
      {isLoading && (
        <div 
          className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10"
          role="alert"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="text-center">
            <div 
              className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-coral border-r-transparent mb-4"
              aria-hidden="true"
            ></div>
            <p id="loading-status">Loading Breathing Exercise...</p>
          </div>
        </div>
      )}
      <div 
        ref={gameContainerRef} 
        className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-b from-blue-50 to-blue-100 px-2"
      >
        {!isLoading && (
          <>
            <div className={`text-center ${isMobile ? 'mb-2 mt-2' : 'mb-4 mt-2'}`}>
              <h2 
                className={`font-bold ${isMobile ? 'text-xl' : 'text-2xl'}`}
                id="breathing-title"
              >Breathing Exercise</h2>
              <p 
                className={`${isMobile ? 'text-sm' : 'text-base'} text-gray-600`}
                aria-live="polite"
              >Cycles completed: {cyclesCompleted}</p>
            </div>
            
            <div className={`w-full ${isMobile ? 'max-w-xs' : 'max-w-md'} mx-auto`}>
              <BreathingCanvas 
                breathingPhase={breathingPhase}
                secondsLeft={secondsLeft}
                cyclesCompleted={cyclesCompleted}
                isMobile={isMobile}
              />
            </div>
            
            <BreathingStatus 
              breathingPhase={breathingPhase}
              secondsLeft={secondsLeft}
              isMobile={isMobile}
            />
            
            <div className={`mt-auto ${isMobile ? 'mb-2' : 'mb-4'}`}>
              <BreathingInstructions isMobile={isMobile} />
            </div>
          </>
        )}
      </div>

      {isMobile && (
        <MobileInstructions gameType="Breathing" />
      )}
    </div>
  );
};

export default BreathingGame;

